<template>
    <div class="green-book">
        <div class="page-header">
            <div>
                <h2 class="page-title">绿书营销</h2>
                <p class="page-desc">管理知识库和内容，支持批量导入和绑定门店/商品</p>
            </div>
            <el-button type="primary" size="large" @click="openAddKnowledgeDialog">
                <el-icon>
                    <Plus />
                </el-icon>
                新建知识库
            </el-button>
        </div>

        <!-- 知识库列表 -->
        <div class="knowledge-list">
            <div v-for="knowledge in knowledgeList" :key="knowledge.id" class="knowledge-card"
                @click="selectKnowledge(knowledge)">
                <div class="knowledge-cover">
                    <img :src="knowledge.cover_image || defaultCover" alt="cover" />
                </div>
                <div class="knowledge-info">
                    <h3 class="knowledge-name">{{ knowledge.knowledge_name }}</h3>
                    <p class="knowledge-desc">{{ knowledge.knowledge_description || '暂无描述' }}</p>
                    <div class="knowledge-meta">
                        <span><el-icon>
                                <Document />
                            </el-icon> {{ knowledge.content_count || 0 }}篇内容</span>
                        <span><el-icon>
                                <Link />
                            </el-icon> {{ getBindTypeText(knowledge.bind_type) }}</span>
                    </div>
                </div>
                <div class="knowledge-actions" @click.stop>
                    <el-button type="primary" link @click="editKnowledge(knowledge)">
                        <el-icon>
                            <Edit />
                        </el-icon> 编辑
                    </el-button>
                    <el-button type="success" link @click="copyKnowledge(knowledge)">
                        <el-icon>
                            <CopyDocument />
                        </el-icon> 复制
                    </el-button>
                    <el-popconfirm :title="`确定删除知识库「${knowledge.knowledge_name}」吗？`"
                        @confirm="handleDeleteKnowledge(knowledge.id)">
                        <template #reference>
                            <el-button type="danger" link>
                                <el-icon>
                                    <Delete />
                                </el-icon> 删除
                            </el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </div>

            <div v-if="knowledgeList.length === 0 && !loading" class="empty-state">
                <el-empty description="暂无知识库，点击左上角新建" />
            </div>
        </div>

        <!-- 知识库编辑弹窗 -->
        <el-dialog v-model="knowledgeDialogVisible" :title="knowledgeDialogTitle" width="700px" destroy-on-close>
            <el-form :model="knowledgeForm" :rules="knowledgeRules" ref="knowledgeFormRef" label-width="100px">
                <el-form-item label="知识库名称" prop="knowledge_name" required>
                    <el-input v-model="knowledgeForm.knowledge_name" placeholder="请输入知识库名称" />
                </el-form-item>
                <el-form-item label="知识库描述">
                    <el-input v-model="knowledgeForm.knowledge_description" type="textarea" :rows="2"
                        placeholder="请输入知识库描述" />
                </el-form-item>
                <el-form-item label="封面图片">
                    <el-upload :file-list="coverFiles" action="#" :limit="1" accept="image/*" :auto-upload="false"
                        :on-change="handleCoverChange" :on-remove="() => knowledgeForm.cover_image = ''"
                        list-type="picture-card">
                        <el-icon>
                            <Plus />
                        </el-icon>
                    </el-upload>
                </el-form-item>
                <el-form-item label="绑定类型">
                    <el-select v-model="knowledgeForm.bind_type" placeholder="请选择绑定类型" style="width: 100%"
                        @change="onBindTypeChange">
                        <el-option label="全局（全部场景）" value="all" />
                        <el-option label="绑定分类" value="category" />
                        <el-option label="绑定商品" value="product" />
                        <el-option label="绑定门店" value="store" />
                        <el-option label="绑定分店" value="branch" />
                    </el-select>
                </el-form-item>
                <el-form-item label="绑定对象" v-if="knowledgeForm.bind_type && knowledgeForm.bind_type !== 'all'">
                    <el-select v-model="knowledgeForm.bind_ids" multiple filterable placeholder="请选择要绑定的对象"
                        style="width: 100%">
                        <el-option v-for="item in bindOptions" :key="item.id" :label="item.label" :value="item.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="knowledgeForm.sort_order" :min="0" :max="999" />
                </el-form-item>
                <el-form-item label="状态">
                    <el-switch v-model="knowledgeForm.status" :active-value="1" :inactive-value="0" active-text="启用"
                        inactive-text="禁用" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="knowledgeDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitKnowledge" :loading="submitting">确 定</el-button>
            </template>
        </el-dialog>

        <!-- 内容管理区域 -->
        <div class="content-section" v-if="currentKnowledge">
            <div class="content-header">
                <h3>{{ currentKnowledge.knowledge_name }} - 内容管理</h3>
                <div class="content-actions">
                    <el-button type="primary" @click="openAddContentDialog">
                        <el-icon>
                            <Plus />
                        </el-icon> 添加内容
                    </el-button>
                    <el-button type="success" @click="openBatchImportDialog">
                        <el-icon>
                            <Upload />
                        </el-icon> 批量导入
                    </el-button>
                    <el-button type="info" @click="exportContents">
                        <el-icon>
                            <Download />
                        </el-icon> 导出内容
                    </el-button>
                </div>
            </div>

            <!-- 内容列表 -->
            <div class="content-list">
                <div v-for="content in contents" :key="content.id" class="content-item">
                    <div class="content-preview">
                        <div class="content-title">
                            <span class="drag-handle">
                                <el-icon>
                                    <Rank />
                                </el-icon>
                            </span>
                            <h4>{{ content.title }}</h4>
                            <el-tag :type="content.status === 1 ? 'success' : 'danger'" size="small">
                                {{ content.status === 1 ? '已发布' : '草稿' }}
                            </el-tag>
                        </div>
                        <div class="content-summary">{{ truncateText(content.content, 100) }}</div>
                        <div class="content-meta">
                            <span v-if="content.topics && content.topics.length">
                                <el-icon>
                                    <PriceTag />
                                </el-icon>
                                <el-tag v-for="topic in content.topics.slice(0, 3)" :key="topic" size="small" round>{{
                                    topic
                                    }}</el-tag>
                            </span>
                            <span><el-icon>
                                    <Picture />
                                </el-icon> {{ content.images?.length || 0 }}张图片</span>
                            <span><el-icon>
                                    <View />
                                </el-icon> {{ content.view_count || 0 }}次浏览</span>
                        </div>
                    </div>
                    <div class="content-actions">
                        <el-button type="primary" link size="small" @click="editContent(content)">
                            <el-icon>
                                <Edit />
                            </el-icon> 编辑
                        </el-button>
                        <el-button type="warning" link size="small" @click="toggleContentStatus(content)">
                            <el-icon>
                                <Switch />
                            </el-icon> {{ content.status === 1 ? '下架' : '发布' }}
                        </el-button>
                        <el-popconfirm :title="`确定删除内容「${content.title}」吗？`" @confirm="handleDeleteContent(content.id)">
                            <template #reference>
                                <el-button type="danger" link size="small">
                                    <el-icon>
                                        <Delete />
                                    </el-icon> 删除
                                </el-button>
                            </template>
                        </el-popconfirm>
                    </div>
                </div>

                <div v-if="contents.length === 0 && !contentLoading" class="empty-content">
                    <el-empty description="暂无内容，点击上方添加" />
                </div>
            </div>

            <!-- 内容分页 -->
            <div class="content-pagination" v-if="contentPagination.total > contentPagination.limit">
                <el-pagination v-model:current-page="contentPagination.page" v-model:page-size="contentPagination.limit"
                    :total="contentPagination.total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next"
                    @size-change="fetchContents" @current-change="fetchContents" />
            </div>
        </div>

        <!-- 内容编辑弹窗 -->
        <el-dialog v-model="contentDialogVisible" :title="contentDialogTitle" width="950px" destroy-on-close>
            <el-form :model="contentForm" :rules="contentRules" ref="contentFormRef" label-width="100px">
                <el-form-item label="标题" prop="title" required>
                    <el-input v-model="contentForm.title" placeholder="请输入标题" />
                </el-form-item>
                <el-form-item label="话题标签">
                    <el-select v-model="contentForm.topics" multiple filterable allow-create default-first-option
                        placeholder="请输入话题标签" style="width: 100%" />
                </el-form-item>
                <el-form-item label="AI提示词">
                    <el-select v-model="contentForm.ai_prompts" multiple filterable allow-create default-first-option
                        placeholder="请选择或输入AI提示词" style="width: 100%">
                    </el-select>
                    <div class="ai-prompts-buttons" v-if="aiPromptList && aiPromptList.length > 0">
                        <span class="prompts-label">快速填充：</span>
                        <el-button v-for="prompt in aiPromptList" :key="prompt.id" size="small" plain
                            @click="fillAiPrompt(prompt.content)" class="ai-prompt-btn">
                            {{ prompt.title }}
                        </el-button>
                    </div>
                    <div v-else-if="aiPromptsLoading" class="ai-prompts-loading">
                        <span class="prompts-label">正在加载提示词...</span>
                    </div>
                    <div v-else-if="(!aiPromptList || aiPromptList.length === 0) && !aiPromptsLoading"
                        class="ai-prompts-loading">
                        <span class="prompts-label">暂无提示词</span>
                    </div>
                </el-form-item>
                <el-form-item label="图片上传">
                    <el-upload :file-list="contentImageFiles" action="#" :limit="20" accept="image/*"
                        :auto-upload="false" :on-change="handleContentImageChange" :on-remove="handleContentImageRemove"
                        list-type="picture-card" multiple>
                        <el-icon>
                            <Plus />
                        </el-icon>
                        <template #tip>
                            <div class="upload-tip">支持批量上传，最多20张，点击或拖拽上传</div>
                        </template>
                    </el-upload>
                </el-form-item>
                <el-form-item label="内容">
                    <div class="editor-wrapper">
                        <QuillEditor ref="contentQuillRef" v-model:content="contentForm.content" contentType="html"
                            :options="editorOptions" style="height: 300px" />
                    </div>
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="contentForm.sort_order" :min="0" :max="999" />
                </el-form-item>
                <el-form-item label="状态">
                    <el-switch v-model="contentForm.status" :active-value="1" :inactive-value="0" active-text="发布"
                        inactive-text="草稿" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="contentDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitContent" :loading="contentSubmitting">确 定</el-button>
            </template>
        </el-dialog>

        <!-- 批量导入弹窗 -->
        <el-dialog v-model="batchImportVisible" title="批量导入内容" width="800px">
            <el-tabs v-model="importTab">
                <el-tab-pane label="CSV导入" name="csv">
                    <div class="import-tip">
                        <p>CSV格式说明：第一行为表头，包含 title, content, topics, ai_prompts, images 列</p>
                        <p>topics 和 ai_prompts 多个值用 | 或 , 分隔</p>
                        <p>images 多个图片链接用 | 分隔</p>
                        <el-button type="primary" link @click="downloadTemplate">下载模板</el-button>
                    </div>
                    <el-upload :auto-upload="false" :on-change="handleCsvUpload" accept=".csv" drag>
                        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
                        <div class="el-upload__text">将CSV文件拖到此处，或<em>点击上传</em></div>
                    </el-upload>
                </el-tab-pane>
                <el-tab-pane label="TXT导入" name="txt">
                    <div class="import-tip">
                        <p>TXT格式说明：每行格式为 "标题|内容|话题1,话题2|提示词1,提示词2|图片1,图片2"</p>
                        <el-button type="primary" link @click="downloadTxtTemplate">下载TXT模板</el-button>
                    </div>
                    <el-upload :auto-upload="false" :on-change="handleTxtUpload" accept=".txt" drag>
                        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
                        <div class="el-upload__text">将TXT文件拖到此处，或<em>点击上传</em></div>
                    </el-upload>
                    <div v-if="txtFileName" class="txt-file-info">
                        已选择文件：{{ txtFileName }}
                    </div>
                </el-tab-pane>
                <el-tab-pane label="批量分配图片" name="assign_images">
                    <div class="import-tip">
                        <p>为选中的内容批量添加图片（不覆盖原有图片）</p>
                    </div>
                    <div class="assign-images-section">
                        <div class="assign-images-header">
                            <span>选择要添加图片的内容：</span>
                            <el-button type="primary" link @click="toggleSelectAll">全选/取消</el-button>
                        </div>
                        <div class="content-checkbox-list">
                            <el-checkbox-group v-model="selectedContentIds">
                                <el-checkbox v-for="content in contents" :key="content.id" :label="content.id">
                                    {{ content.title }}
                                </el-checkbox>
                            </el-checkbox-group>
                        </div>
                        <div class="assign-images-upload">
                            <el-upload :file-list="assignImageFiles" action="#" :limit="50" accept="image/*"
                                :auto-upload="false" :on-change="handleAssignImageChange"
                                :on-remove="handleAssignImageRemove" list-type="picture-card" multiple>
                                <el-icon>
                                    <Plus />
                                </el-icon>
                                <template #tip>
                                    <div class="upload-tip">选择要添加的图片，将添加到选中的内容中</div>
                                </template>
                            </el-upload>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>
            <template #footer>
                <el-button @click="batchImportVisible = false">取消</el-button>
                <el-button type="primary" @click="submitBatchImport" :loading="batchImporting">开始导入</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'

import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { Plus, Edit, Delete, Document, Link, CopyDocument, Upload, Download, Picture, View, PriceTag, Switch, Rank, UploadFilled } from '@element-plus/icons-vue'

export default {
    name: 'GreenBookPage',
    components: { QuillEditor, Plus, Edit, Delete, Document, Link, CopyDocument, Upload, Download, Picture, View, PriceTag, Switch, Rank, UploadFilled },
    setup() {
        const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
        const agentId = ref(userInfo.agent_id || 0)
        const storeId = ref(userInfo.id)

        const loading = ref(false)
        const submitting = ref(false)
        const contentLoading = ref(false)
        const contentSubmitting = ref(false)
        const batchImporting = ref(false)
        const knowledgeList = ref([])
        const currentKnowledge = ref(null)
        const contents = ref([])
        const defaultCover = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

        const knowledgeDialogVisible = ref(false)
        const contentDialogVisible = ref(false)
        const batchImportVisible = ref(false)
        const isEditKnowledge = ref(false)
        const isEditContent = ref(false)
        const knowledgeFormRef = ref(null)
        const contentFormRef = ref(null)
        const contentQuillRef = ref(null)
        const coverFiles = ref([])
        const contentImageFiles = ref([])
        const batchImageFiles = ref([])
        const assignImageFiles = ref([])
        const selectedContentIds = ref([])
        const importTab = ref('csv')
        const csvData = ref(null)
        const txtData = ref('')
        const aiPromptList = ref([])
        const aiPromptsLoading = ref(false)

        const knowledgeForm = reactive({
            id: null,
            knowledge_name: '',
            knowledge_description: '',
            cover_image: '',
            bind_type: 'all',
            bind_ids: [],
            status: 1,
            sort_order: 0
        })

        const contentForm = reactive({
            id: null,
            title: '',
            content: '',
            topics: [],
            images: [],
            ai_prompts: [],
            status: 1,
            sort_order: 0
        })

        const bindOptions = ref([])
        const categoryOptions = ref([])
        const productOptions = ref([])
        const storeOptions = ref([])
        const branchOptions = ref([])

        const contentPagination = reactive({
            page: 1,
            limit: 20,
            total: 0
        })

        const knowledgeRules = {
            knowledge_name: [{ required: true, message: '请输入知识库名称', trigger: 'blur' }]
        }

        const contentRules = {
            title: [{ required: true, message: '请输入标题', trigger: 'blur' }]
        }

        const knowledgeDialogTitle = ref('新建知识库')
        const contentDialogTitle = ref('添加内容')

        // AI提示词分组
        const aiPromptGroups = computed(() => {
            const groups = {}
            if (!aiPromptList.value || !Array.isArray(aiPromptList.value)) {
                return []
            }
            aiPromptList.value.forEach(item => {
                const category = item.title?.split(' ')[0] || '其他'
                if (!groups[category]) groups[category] = []
                groups[category].push(item)
            })
            return Object.keys(groups).map(key => ({
                label: key,
                options: groups[key]
            }))
        })


        const txtFileName = ref('')

        const handleTxtUpload = (file) => {
            const reader = new FileReader()
            txtFileName.value = file.name
            reader.onload = (e) => {
                txtData.value = e.target.result
                ElMessage.success('TXT文件已加载')
            }
            reader.readAsText(file.raw, 'UTF-8')
        }

        // 填充AI提示词到输入框（追加）
        // 填充AI提示词到输入框（追加）
        const fillAiPrompt = (promptContent) => {
            // 检查是否已存在
            if (!contentForm.ai_prompts.includes(promptContent)) {
                contentForm.ai_prompts.push(promptContent)
                ElMessage.success('已添加提示词')
            } else {
                ElMessage.warning('该提示词已存在')
            }
        }

        // 获取AI提示词
        const fetchAiPrompts = async () => {
            aiPromptsLoading.value = true
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/ai_prompts_api.php`)
                if (response.data.code === 200) {
                    aiPromptList.value = response.data.data
                    console.log('AI提示词加载成功:', aiPromptList.value.length) // 调试日志
                }
            } catch (error) {
                console.error('获取AI提示词失败:', error)
            } finally {
                aiPromptsLoading.value = false
            }
        }

        const uploadImageToOSS = (file) => {
            return new Promise((resolve, reject) => {
                if (!file.type.startsWith('image/')) {
                    reject(new Error('只能上传图片文件'))
                    return
                }
                if (file.size / 1024 / 1024 > 5) {
                    reject(new Error('图片大小不能超过 5MB'))
                    return
                }
                getUploadSignature('image')
                    .then(signature => uploadToOSS(file, signature))
                    .then(ossRes => resolve(ossRes.url))
                    .catch(error => reject(error))
            })
        }

        const editorOptions = {
            modules: {
                toolbar: {
                    container: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote', 'code-block'],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ script: 'sub' }, { script: 'super' }],
                        [{ indent: '-1' }, { indent: '+1' }],
                        [{ direction: 'rtl' }],
                        [{ size: ['small', false, 'large', 'huge'] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ color: [] }, { background: [] }],
                        [{ font: [] }],
                        [{ align: [] }],
                        ['clean'],
                        ['link', 'image', 'video']
                    ],
                    handlers: {
                        image: function () {
                            const input = document.createElement('input')
                            input.setAttribute('type', 'file')
                            input.setAttribute('accept', 'image/*')
                            input.click()
                            input.onchange = async () => {
                                const file = input.files[0]
                                if (!file) return
                                const loading = ElLoading.service({ text: '上传图片中...', background: 'rgba(0, 0, 0, 0.7)' })
                                try {
                                    const range = this.quill.getSelection(true)
                                    const url = await uploadImageToOSS(file)
                                    this.quill.insertEmbed(range.index, 'image', url)
                                    this.quill.setSelection(range.index + 1)
                                    ElMessage.success('图片上传成功')
                                } catch (error) {
                                    ElMessage.error(error.message || '图片上传失败')
                                } finally {
                                    loading.close()
                                }
                            }
                        }
                    }
                }
            },
            placeholder: '请输入内容...',
            theme: 'snow'
        }

        const truncateText = (text, length) => {
            if (!text) return ''
            const plainText = text.replace(/<[^>]*>/g, '')
            return plainText.length > length ? plainText.substring(0, length) + '...' : plainText
        }

        const getBindTypeText = (type) => {
            const map = { all: '全局', category: '分类', product: '商品', store: '门店', branch: '分店' }
            return map[type] || '全局'
        }

        // 获取分类列表
        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/category_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, type: 'product', limit: 100 }
                })
                if (response.data.code === 200) {
                    categoryOptions.value = response.data.data.map(c => ({ id: c.id, label: c.category_name }))
                }
            } catch (error) {
                console.error('获取分类失败:', error)
            }
        }

        // 获取商品列表
        const fetchProducts = async () => {
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
                })
                if (response.data.code === 200) {
                    productOptions.value = response.data.data.list.map(p => ({ id: p.id, label: p.product_name }))
                }
            } catch (error) {
                console.error('获取商品失败:', error)
            }
        }

        // 获取门店列表
        const fetchStores = async () => {
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
                })
                if (response.data.code === 200) {
                    storeOptions.value = response.data.data.list.map(s => ({ id: s.id, label: s.store_name }))
                }
            } catch (error) {
                console.error('获取门店失败:', error)
            }
        }

        // 获取分店列表
        const fetchBranches = async () => {
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
                })
                if (response.data.code === 200) {
                    branchOptions.value = response.data.data.list.map(b => ({ id: b.id, label: b.branch_name }))
                }
            } catch (error) {
                console.error('获取分店失败:', error)
            }
        }

        const onBindTypeChange = () => {
            const type = knowledgeForm.bind_type
            if (type === 'category') bindOptions.value = categoryOptions.value
            else if (type === 'product') bindOptions.value = productOptions.value
            else if (type === 'store') bindOptions.value = storeOptions.value
            else if (type === 'branch') bindOptions.value = branchOptions.value
            else bindOptions.value = []
            knowledgeForm.bind_ids = []
        }

        // 获取知识库列表
        const fetchKnowledgeList = async () => {
            loading.value = true
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/green_book_api.php`, {
                    params: { action: 'knowledge', agent_id: agentId.value, store_id: storeId.value, limit: 100 }
                })
                if (response.data.code === 200) {
                    knowledgeList.value = response.data.data.list
                }
            } catch (error) {
                console.error('获取知识库失败:', error)
                ElMessage.error('获取数据失败')
            } finally {
                loading.value = false
            }
        }

        // 选择知识库
        const selectKnowledge = async (knowledge) => {
            currentKnowledge.value = knowledge
            contentPagination.page = 1
            await fetchContents()
        }

        // 获取内容列表
        const fetchContents = async () => {
            if (!currentKnowledge.value) return
            contentLoading.value = true
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/green_book_api.php`, {
                    params: {
                        action: 'content',
                        knowledge_id: currentKnowledge.value.id,
                        store_id: storeId.value,
                        page: contentPagination.page,
                        limit: contentPagination.limit
                    }
                })
                if (response.data.code === 200) {
                    contents.value = response.data.data.list
                    contentPagination.total = response.data.data.pagination.total
                }
            } catch (error) {
                console.error('获取内容失败:', error)
            } finally {
                contentLoading.value = false
            }
        }

        const openAddKnowledgeDialog = () => {
            isEditKnowledge.value = false
            knowledgeDialogTitle.value = '新建知识库'
            Object.assign(knowledgeForm, {
                id: null, knowledge_name: '', knowledge_description: '', cover_image: '',
                bind_type: 'all', bind_ids: [], status: 1, sort_order: 0
            })
            coverFiles.value = []
            knowledgeDialogVisible.value = true
        }

        const editKnowledge = (knowledge) => {
            isEditKnowledge.value = true
            knowledgeDialogTitle.value = '编辑知识库'
            Object.assign(knowledgeForm, {
                id: knowledge.id,
                knowledge_name: knowledge.knowledge_name,
                knowledge_description: knowledge.knowledge_description || '',
                cover_image: knowledge.cover_image || '',
                bind_type: knowledge.bind_type || 'all',
                bind_ids: knowledge.bind_ids || [],
                status: knowledge.status,
                sort_order: knowledge.sort_order
            })
            if (knowledge.cover_image) {
                coverFiles.value = [{ uid: Date.now(), name: 'cover', url: knowledge.cover_image }]
            }
            // 先设置绑定选项，再设置 bind_ids，避免被重置
            onBindTypeChange()
            // 确保 bind_ids 在 onBindTypeChange 之后重新赋值
            knowledgeForm.bind_ids = knowledge.bind_ids || []
            knowledgeDialogVisible.value = true
        }

        const copyKnowledge = async (knowledge) => {
            const loadingInstance = ElLoading.service({ text: '复制中...' })
            try {
                const copyData = {
                    agent_id: agentId.value, store_id: storeId.value,
                    knowledge_name: `${knowledge.knowledge_name}(副本)`,
                    knowledge_description: knowledge.knowledge_description,
                    cover_image: knowledge.cover_image, bind_type: knowledge.bind_type,
                    bind_ids: knowledge.bind_ids, status: 1, sort_order: 0
                }
                const response = await axios.post(`${config.BASE_URL}/shop/api/green_book_api.php?action=knowledge`, copyData)
                if (response.data.code === 200) {
                    ElMessage.success('复制成功')
                    fetchKnowledgeList()
                } else {
                    ElMessage.error(response.data.message || '复制失败')
                }
            } catch (error) {
                console.error('复制失败:', error)
                ElMessage.error('复制失败')
            } finally {
                loadingInstance.close()
            }
        }

        const submitKnowledge = async () => {
            if (!knowledgeFormRef.value) return
            await knowledgeFormRef.value.validate(async (valid) => {
                if (!valid) return
                submitting.value = true
                const loadingInstance = ElLoading.service({ text: '保存中...' })
                try {
                    const payload = {
                        agent_id: agentId.value, store_id: storeId.value,
                        knowledge_name: knowledgeForm.knowledge_name,
                        knowledge_description: knowledgeForm.knowledge_description,
                        cover_image: knowledgeForm.cover_image,
                        bind_type: knowledgeForm.bind_type,
                        bind_ids: knowledgeForm.bind_ids,
                        status: knowledgeForm.status,
                        sort_order: knowledgeForm.sort_order
                    }
                    let response
                    if (isEditKnowledge.value) {
                        payload.id = knowledgeForm.id
                        response = await axios.put(`${config.BASE_URL}/shop/api/green_book_api.php?action=knowledge`, payload)
                    } else {
                        response = await axios.post(`${config.BASE_URL}/shop/api/green_book_api.php?action=knowledge`, payload)
                    }
                    if (response.data.code === 200) {
                        ElMessage.success(isEditKnowledge.value ? '更新成功' : '创建成功')
                        knowledgeDialogVisible.value = false
                        fetchKnowledgeList()
                    } else {
                        ElMessage.error(response.data.message || '操作失败')
                    }
                } catch (error) {
                    console.error('提交失败:', error)
                    ElMessage.error('操作失败')
                } finally {
                    submitting.value = false
                    loadingInstance.close()
                }
            })
        }

        const handleDeleteKnowledge = async (id) => {
            const loadingInstance = ElLoading.service({ text: '删除中...' })
            try {
                const response = await axios.delete(`${config.BASE_URL}/shop/api/green_book_api.php?action=knowledge`, {
                    data: { id, store_id: storeId.value }
                })
                if (response.data.code === 200) {
                    ElMessage.success('删除成功')
                    if (currentKnowledge.value?.id === id) {
                        currentKnowledge.value = null
                        contents.value = []
                    }
                    fetchKnowledgeList()
                } else {
                    ElMessage.error(response.data.message || '删除失败')
                }
            } catch (error) {
                console.error('删除失败:', error)
                ElMessage.error('删除失败')
            } finally {
                loadingInstance.close()
            }
        }

        const handleCoverChange = async (file) => {
            if (!file.raw) return
            const url = await uploadImageToOSS(file.raw)
            if (url) {
                knowledgeForm.cover_image = url
                coverFiles.value = [{ uid: file.uid, name: file.name, url }]
                ElMessage.success('上传成功')
            }
        }

        // 修改 handleContentImageChange 函数
        const handleContentImageChange = async (file) => {
            if (!file.raw) return
            const url = await uploadImageToOSS(file.raw)
            if (url) {
                // 添加到 images 数组
                contentForm.images.push(url)
                // 重新构建文件列表，确保所有图片都有正确的 url
                contentImageFiles.value = contentForm.images.map((imgUrl, idx) => ({
                    uid: Date.now() + idx,
                    name: `image-${idx}`,
                    url: imgUrl
                }))
                ElMessage.success('上传成功')
            }
        }

        const handleContentImageRemove = (file) => {
            const index = contentForm.images.findIndex(img => img === file.url)
            if (index !== -1) {
                contentForm.images.splice(index, 1)
                // 重新构建文件列表
                contentImageFiles.value = contentForm.images.map((imgUrl, idx) => ({
                    uid: Date.now() + idx,
                    name: `image-${idx}`,
                    url: imgUrl
                }))
            }
        }

        const openAddContentDialog = async () => {
            isEditContent.value = false
            contentDialogTitle.value = '添加内容'
            Object.assign(contentForm, {
                id: null,
                title: '',
                content: '',
                topics: [],
                images: [],
                ai_prompts: [],
                status: 1,
                sort_order: 0
            })
            contentImageFiles.value = []
            // 先加载数据，再打开弹窗
            await fetchAiPrompts()
            contentDialogVisible.value = true
        }

        const editContent = async (content) => {
            isEditContent.value = true
            contentDialogTitle.value = '编辑内容'
            Object.assign(contentForm, {
                id: content.id,
                title: content.title || '',
                content: content.content || '',
                topics: content.topics || [],
                images: content.images || [],
                ai_prompts: content.ai_prompts || [],
                status: content.status !== undefined ? content.status : 1,
                sort_order: content.sort_order || 0
            })
            // 修复回显：根据 images 数组构建文件列表
            contentImageFiles.value = (content.images && Array.isArray(content.images) ? content.images : []).map((url, idx) => ({
                uid: Date.now() + idx,
                name: `image-${idx}`,
                url: url
            }))
            // 打开弹窗前先加载AI提示词
            await fetchAiPrompts()
            contentDialogVisible.value = true
        }

        const submitContent = async () => {
            if (!contentFormRef.value) return
            await contentFormRef.value.validate(async (valid) => {
                if (!valid) return
                contentSubmitting.value = true
                const loadingInstance = ElLoading.service({ text: '保存中...' })
                try {
                    const payload = {
                        agent_id: agentId.value, store_id: storeId.value,
                        knowledge_id: currentKnowledge.value.id,
                        title: contentForm.title, content: contentForm.content,
                        topics: contentForm.topics, images: contentForm.images,
                        ai_prompts: contentForm.ai_prompts, status: contentForm.status,
                        sort_order: contentForm.sort_order
                    }
                    let response
                    if (isEditContent.value) {
                        payload.id = contentForm.id
                        response = await axios.put(`${config.BASE_URL}/shop/api/green_book_api.php?action=content`, payload)
                    } else {
                        response = await axios.post(`${config.BASE_URL}/shop/api/green_book_api.php?action=content`, payload)
                    }
                    if (response.data.code === 200) {
                        ElMessage.success(isEditContent.value ? '更新成功' : '添加成功')
                        contentDialogVisible.value = false
                        await fetchContents()
                    } else {
                        ElMessage.error(response.data.message || '操作失败')
                    }
                } catch (error) {
                    console.error('提交失败:', error)
                    ElMessage.error('操作失败')
                } finally {
                    contentSubmitting.value = false
                    loadingInstance.close()
                }
            })
        }

        const toggleContentStatus = async (content) => {
            const newStatus = content.status === 1 ? 0 : 1
            const loadingInstance = ElLoading.service({ text: '处理中...' })
            try {
                // 先获取完整的内容数据
                const detailResponse = await axios.get(`${config.BASE_URL}/shop/api/green_book_api.php`, {
                    params: {
                        action: 'content',
                        knowledge_id: currentKnowledge.value.id,
                        store_id: storeId.value,
                        id: content.id
                    }
                })
                if (detailResponse.data.code === 200) {
                    const fullContent = detailResponse.data.data
                    const response = await axios.put(`${config.BASE_URL}/shop/api/green_book_api.php?action=content`, {
                        id: content.id,
                        store_id: storeId.value,
                        title: fullContent.title,
                        content: fullContent.content || '',
                        topics: fullContent.topics || [],
                        images: fullContent.images || [],
                        ai_prompts: fullContent.ai_prompts || [],
                        status: newStatus,
                        sort_order: fullContent.sort_order || 0,
                        old_status: content.status
                    })
                    if (response.data.code === 200) {
                        ElMessage.success(newStatus === 1 ? '已发布' : '已下架')
                        await fetchContents()
                    } else {
                        ElMessage.error(response.data.message || '操作失败')
                    }
                }
            } catch (error) {
                console.error('操作失败:', error)
                ElMessage.error('操作失败')
            } finally {
                loadingInstance.close()
            }
        }

        const handleDeleteContent = async (id) => {
            const loadingInstance = ElLoading.service({ text: '删除中...' })
            try {
                const response = await axios.delete(`${config.BASE_URL}/shop/api/green_book_api.php?action=content`, {
                    data: { id, store_id: storeId.value }
                })
                if (response.data.code === 200) {
                    ElMessage.success('删除成功')
                    await fetchContents()
                } else {
                    ElMessage.error(response.data.message || '删除失败')
                }
            } catch (error) {
                console.error('删除失败:', error)
                ElMessage.error('删除失败')
            } finally {
                loadingInstance.close()
            }
        }

        const openBatchImportDialog = () => {
            importTab.value = 'csv'
            csvData.value = null
            txtData.value = ''
            batchImageFiles.value = []
            assignImageFiles.value = []
            selectedContentIds.value = []
            batchImportVisible.value = true
        }

        const downloadTemplate = () => {
            const template = 'title,content,topics,ai_prompts,images\n示例标题,示例内容,话题1|话题2,提示词1|提示词2,https://example.com/image1.jpg|https://example.com/image2.jpg'
            const blob = new Blob(['\uFEFF' + template], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = 'greenbook_template.csv'
            link.click()
            URL.revokeObjectURL(link.href)
        }

        const downloadTxtTemplate = () => {
            const template = '标题|内容|话题1,话题2|提示词1,提示词2|图片1,图片2\n示例标题|示例内容|美食,探店|专业文案|https://example.com/img1.jpg,https://example.com/img2.jpg'
            const blob = new Blob(['\uFEFF' + template], { type: 'text/plain;charset=utf-8;' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = 'greenbook_template.txt'
            link.click()
            URL.revokeObjectURL(link.href)
        }

        const handleCsvUpload = (file) => {
            const reader = new FileReader()
            reader.onload = (e) => {
                csvData.value = e.target.result
                ElMessage.success('CSV文件已加载')
            }
            reader.readAsText(file.raw, 'UTF-8')
        }

        const handleBatchImageChange = async (file) => {
            if (!file.raw) return
            const url = await uploadImageToOSS(file.raw)
            if (url) {
                // 将新上传的图片添加到现有列表
                batchImageFiles.value.push({
                    uid: Date.now(),
                    name: file.name,
                    url: url,
                    status: 'success'
                })
                ElMessage.success('上传成功')
            }
        }

        const handleBatchImageRemove = (file) => {
            batchImageFiles.value = batchImageFiles.value.filter(f => f.uid !== file.uid)
        }

        // 修改 handleAssignImageChange 函数
        const handleAssignImageChange = async (file) => {
            if (!file.raw) return
            const url = await uploadImageToOSS(file.raw)
            if (url) {
                // 将新上传的图片添加到现有列表
                assignImageFiles.value.push({
                    uid: Date.now(),
                    name: file.name,
                    url: url,
                    status: 'success'
                })
                ElMessage.success('上传成功')
            }
        }

        const handleAssignImageRemove = (file) => {
            assignImageFiles.value = assignImageFiles.value.filter(f => f.uid !== file.uid)
        }

        const toggleSelectAll = () => {
            if (selectedContentIds.value.length === contents.value.length) {
                selectedContentIds.value = []
            } else {
                selectedContentIds.value = contents.value.map(c => c.id)
            }
        }

        const submitBatchImport = async () => {
            batchImporting.value = true
            const loadingInstance = ElLoading.service({ text: '导入中...' })
            try {
                let payload = {
                    agent_id: agentId.value,
                    store_id: storeId.value,
                    knowledge_id: currentKnowledge.value.id,
                    import_type: importTab.value
                }

                if (importTab.value === 'csv' && csvData.value) {
                    payload.csv_data = csvData.value
                } else if (importTab.value === 'txt' && txtData.value) {
                    payload.txt_data = txtData.value
                } // 修改 submitBatchImport 中的 assign_images 部分
                else if (importTab.value === 'assign_images' && selectedContentIds.value.length > 0 && assignImageFiles.value.length > 0) {
                    const imageUrls = assignImageFiles.value.map(f => f.url)
                    payload.import_type = 'assign_images'
                    payload.content_ids = selectedContentIds.value
                    payload.image_urls = imageUrls
                } else if (importTab.value === 'images' && batchImageFiles.value.length > 0) {
                    const imageUrls = batchImageFiles.value.map(f => f.url)
                    payload.import_type = 'array'
                    payload.data = imageUrls.map(url => ({
                        title: `图片_${new Date().getTime()}_${Math.random().toString(36).substr(2, 4)}`,
                        content: `<img src="${url}" alt="image" />`,
                        images: [url],
                        topics: [],
                        ai_prompts: []
                    }))
                } else {
                    ElMessage.warning('请先选择要导入的数据')
                    return
                }

                const response = await axios.post(`${config.BASE_URL}/shop/api/green_book_api.php?action=batch_import`, payload)
                if (response.data.code === 200) {
                    ElMessage.success(response.data.message)
                    batchImportVisible.value = false
                    await fetchContents()
                } else {
                    ElMessage.error(response.data.message || '导入失败')
                }
            } catch (error) {
                console.error('导入失败:', error)
                ElMessage.error('导入失败')
            } finally {
                batchImporting.value = false
                loadingInstance.close()
            }
        }

        const exportContents = () => {
            if (!currentKnowledge.value) return
            const contentsData = contents.value.map(c => ({
                title: c.title,
                content: c.content.replace(/<[^>]*>/g, ''),
                topics: (c.topics || []).join('|'),
                ai_prompts: (c.ai_prompts || []).join('|'),
                images: (c.images || []).join('|')
            }))
            const headers = ['title', 'content', 'topics', 'ai_prompts', 'images']
            const csvRows = [headers.join(',')]
            for (const row of contentsData) {
                const values = headers.map(header => {
                    let value = row[header] || ''
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                        value = `"${value.replace(/"/g, '""')}"`
                    }
                    return value
                })
                csvRows.push(values.join(','))
            }
            const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = `${currentKnowledge.value.knowledge_name}_contents.csv`
            link.click()
            URL.revokeObjectURL(link.href)
        }

        onMounted(() => {
            fetchCategories()
            fetchProducts()
            fetchStores()
            fetchBranches()
            fetchKnowledgeList()
        })

        return {
            loading, submitting, contentLoading, contentSubmitting, batchImporting,
            knowledgeList, currentKnowledge, contents, defaultCover,
            knowledgeDialogVisible, contentDialogVisible, batchImportVisible,
            isEditKnowledge, isEditContent, knowledgeFormRef, contentFormRef,
            contentQuillRef, coverFiles, contentImageFiles, batchImageFiles, assignImageFiles, selectedContentIds,
            importTab, csvData, txtData,
            knowledgeForm, contentForm, bindOptions,
            contentPagination, knowledgeRules, contentRules,
            knowledgeDialogTitle, contentDialogTitle,
            editorOptions, truncateText, getBindTypeText,
            aiPromptGroups,
            onBindTypeChange, fetchKnowledgeList, selectKnowledge,
            fetchContents, openAddKnowledgeDialog, editKnowledge,
            copyKnowledge, submitKnowledge, handleDeleteKnowledge,
            handleCoverChange, handleContentImageChange, handleContentImageRemove,
            openAddContentDialog, editContent, submitContent,
            toggleContentStatus, handleDeleteContent,
            openBatchImportDialog, downloadTemplate, downloadTxtTemplate, handleCsvUpload,
            handleBatchImageChange, handleBatchImageRemove, handleAssignImageChange, handleAssignImageRemove,
            submitBatchImport, toggleSelectAll,
            exportContents, handleTxtUpload, fillAiPrompt, aiPromptsLoading, aiPromptList
        }
    }
}
</script>

<style scoped lang="scss">
.green-book {
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .page-title {
            font-size: 24px;
            font-weight: 600;
            color: #1a1a2e;
            margin-bottom: 4px;
        }

        .page-desc {
            font-size: 14px;
            color: #8c8c8c;
        }
    }

    .ai-prompts-buttons {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-top: 12px;
        padding-top: 8px;
        border-top: 1px dashed #e8e8e8;

        .prompts-label {
            font-size: 12px;
            color: #8c8c8c;
        }

        .ai-prompt-btn {
            margin: 0;
        }
    }

    .ai-prompts-loading {
        margin-top: 12px;
        padding-top: 8px;
        border-top: 1px dashed #e8e8e8;

        .prompts-label {
            font-size: 12px;
            color: #999;
        }
    }

    .knowledge-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 20px;
        margin-bottom: 30px;

        .knowledge-card {
            background: #fff;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            transition: all 0.3s;
            cursor: pointer;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
            }

            .knowledge-cover {
                height: 140px;
                overflow: hidden;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }

            .knowledge-info {
                padding: 16px;

                .knowledge-name {
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 8px;
                }

                .knowledge-desc {
                    font-size: 13px;
                    color: #666;
                    margin-bottom: 12px;
                    line-height: 1.4;
                }

                .knowledge-meta {
                    display: flex;
                    gap: 16px;
                    font-size: 12px;
                    color: #8c8c8c;

                    .el-icon {
                        margin-right: 4px;
                        vertical-align: middle;
                    }
                }
            }

            .knowledge-actions {
                padding: 12px 16px;
                border-top: 1px solid #f0f0f0;
                display: flex;
                gap: 12px;
            }
        }
    }

    .empty-state {
        padding: 60px 0;
    }

    .content-section {
        margin-top: 30px;
        padding-top: 20px;
        border-top: 1px solid #e8e8e8;

        .content-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;

            h3 {
                font-size: 18px;
                font-weight: 600;
                margin: 0;
            }

            .content-actions {
                display: flex;
                gap: 12px;
            }
        }

        .content-list {
            .content-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px;
                background: #f9fafc;
                border-radius: 12px;
                margin-bottom: 12px;
                transition: all 0.2s;

                &:hover {
                    background: #f0f2f5;
                }

                .content-preview {
                    flex: 1;

                    .content-title {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        margin-bottom: 8px;

                        .drag-handle {
                            cursor: move;
                            color: #8c8c8c;
                        }

                        h4 {
                            margin: 0;
                            font-size: 15px;
                            font-weight: 500;
                        }
                    }

                    .content-summary {
                        font-size: 13px;
                        color: #666;
                        margin-bottom: 8px;
                    }

                    .content-meta {
                        display: flex;
                        gap: 16px;
                        font-size: 12px;
                        color: #8c8c8c;
                        flex-wrap: wrap;

                        .el-icon {
                            margin-right: 4px;
                            vertical-align: middle;
                        }

                        .el-tag {
                            margin-right: 4px;
                        }
                    }
                }

                .content-actions {
                    display: flex;
                    gap: 8px;
                }
            }
        }

        .empty-content {
            padding: 60px 0;
        }

        .content-pagination {
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
        }
    }

    .editor-wrapper {
        width: 100%;

        :deep(.ql-toolbar) {
            border-radius: 8px 8px 0 0;
        }

        :deep(.ql-container) {
            border-radius: 0 0 8px 8px;
            min-height: 250px;
        }
    }

    .import-tip {
        background: #f5f7fa;
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 16px;

        p {
            margin: 4px 0;
            font-size: 13px;
            color: #666;
        }
    }

    .assign-images-section {
        .assign-images-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            padding: 8px 0;
        }

        .content-checkbox-list {
            max-height: 200px;
            overflow-y: auto;
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 16px;

            .el-checkbox {
                display: block;
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        .assign-images-upload {
            margin-top: 16px;
        }
    }

    .upload-tip {
        font-size: 12px;
        color: #8c8c8c;
        margin-top: 8px;
    }
}
</style>